import { api } from './client'

/**
 * TASK-0029 / M14 slice 1: lead conversion funnel — spec section 95 ("Funnel Lead") + section 96
 * ("Conversion Rate"). Mirrors the shape of `GET /api/funnels/lead-conversion`
 * (`FunnelsController.cs`).
 *
 * The "opportunità" stage from spec section 95 is deliberately NOT a field here — `Opportunity`
 * isn't a real entity yet (see `GLOSSARY.md`), and the backend omits the key entirely rather than
 * send a fake `0`. Do not add an `opportunities` field to this interface; its absence is the point.
 */
export interface FunnelStages {
  totalLeads: number
  contacted: number
  qualified: number
  appointmentsSet: number
  appointmentsCompleted: number
  contracts: number
  activations: number
}

/**
 * The six KPI_DICTIONARY.md section 4 conversion rates, plus the disambiguated "Activation Rate
 * (funnel variant)" (Activations / Contracts — NOT the same KPI as the section-27 Activation Rate,
 * which divides by submitted contracts instead). Each is a 0..1 fraction rounded to 4 decimals, or
 * `null` when its denominator is 0 (an empty stage, not a real 0%).
 */
export interface FunnelConversionRates {
  contactRate: number | null
  qualificationRate: number | null
  appointmentRate: number | null
  showRate: number | null
  closingRate: number | null
  leadToSale: number | null
  leadToActivation: number | null
  activationRateOnContracts: number | null
}

export interface LeadConversionFunnel {
  from?: string
  to?: string
  stages: FunnelStages
  conversionRates: FunnelConversionRates
}

export interface FunnelFilters {
  from?: string
  to?: string
}

/**
 * REQ-027-002: spec section 27 ("KPI fondamentali") "Activation Rate" — `GET
 * /api/funnels/practice-rates` (`FunnelsController.GetPracticeRates`). `activationRateVsSubmitted`
 * is Activated contracts / Submitted contracts (KPI_DICTIONARY.md section 2), a DIFFERENT
 * denominator from `FunnelConversionRates.activationRateOnContracts` above (Activations /
 * Contracts) despite the two sharing the word "Activation Rate" — never conflate the two fields.
 * `null` when `submittedContractsCount` is 0 (no data), never a fake 0%.
 *
 * REQ-027-003: same endpoint also emits "Reject Rate" — Rejected contracts / the SAME
 * `submittedContractsCount` denominator above (KPI_DICTIONARY.md section 27's Reject Rate row).
 * `rejectedContractsCount` counts distinct contracts with at least one `Rifiutata` history row
 * (ledger-based, so a rejected-then-reworked contract still counts once) — never `Contract` itself,
 * which does not have a separate `Practice` entity in this model (`ContractStatus.Bozza`'s
 * doc-comment: "The practice exists but has not been submitted yet"). `null` when
 * `submittedContractsCount` is 0, same convention as `activationRateVsSubmitted`.
 *
 * REQ-027-004: same endpoint also emits "Integration Rate" — Practices requiring integration /
 * Total practices (KPI_DICTIONARY.md section 27's Integration Rate row). Unlike the two rates
 * above, its denominator is `totalContractsCount` (ALL ADR-008-visible contracts), NOT
 * `submittedContractsCount` — the dictionary is explicit that this KPI is over total practices, so
 * do not assume all three rates on this endpoint share one denominator.
 * `integrationRequiredContractsCount` counts distinct contracts with at least one
 * `IntegrazioneRichiesta` history row — history-based is mandatory here (not merely a style choice):
 * `IntegrazioneRichiesta` is a transient state whose only outbound edge goes straight back to
 * `DaVerificare`, so `Contract.Status` alone would miss nearly every practice that ever needed an
 * integration. `null` when `totalContractsCount` is 0.
 */
export interface PracticeRates {
  submittedContractsCount: number
  activatedContractsCount: number
  activationRateVsSubmitted: number | null
  rejectedContractsCount: number
  rejectRate: number | null
  totalContractsCount: number
  integrationRequiredContractsCount: number
  integrationRate: number | null
}

export const funnelsApi = {
  leadConversion: (f: FunnelFilters = {}) => {
    const p = new URLSearchParams()
    if (f.from) p.set('from', f.from)
    if (f.to) p.set('to', f.to)
    const qs = p.toString()
    return api<LeadConversionFunnel>(`/api/funnels/lead-conversion${qs ? `?${qs}` : ''}`)
  },
  practiceRates: () => api<PracticeRates>('/api/funnels/practice-rates'),
}
